import { TZDateMini } from '@date-fns/tz';
import {
  endOfYear,
  isAfter,
  isBefore,
  isSameYear,
  startOfYear,
} from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useCalendarContext } from './useCalendarContext.js';
import { useFocus } from './useFocus.js';
import { isTimeframeV2 } from '../../forms-core/time/types.js';
import type { ViewItem } from '../CalendarItem.js';
import type { UseCalendarItemProps } from '../types/shared-types.js';

/** Custom hook that retrieves properties from the year that is passed in. */
export function useYear({ date, itemRef }: UseCalendarItemProps): ViewItem {
  const { state, dispatch } = useCalendarContext();
  const configuredTimezone = getTimezone();

  const intl = useIntl();

  const ariaLabel = useMemo(() => {
    const formatMessage = intl.formatMessage({
      defaultMessage: 'Select year',
      id: 'pRBtzsvqzp1/QqAV',
      description: 'Aria label to add to each year button',
    });

    return `${formatMessage} ${date.getFullYear().toString()}`;
  }, [date, intl]);

  const isCurrent = useMemo(
    () => isSameYear(date, state.today),
    [date, state.today],
  );

  const isFocused = useMemo(
    () => isSameYear(date, state.activeDate),
    [state.activeDate, date],
  );

  const isSelected = useMemo(() => {
    const compareYear = date.getFullYear();

    if (isTimeframeV2(state.selectedDate, state.type)) {
      const { from, to } = state.selectedDate;

      const fromYear = from
        ? new TZDateMini(from.absoluteDate, configuredTimezone).getFullYear()
        : null;
      const toYear = to
        ? new TZDateMini(to.absoluteDate, configuredTimezone).getFullYear()
        : null;

      return fromYear === compareYear || toYear === compareYear;
    }

    const selectedYear = state.selectedDate
      ? new TZDateMini(
          state.selectedDate.absoluteDate,
          configuredTimezone,
        ).getFullYear()
      : null;

    return compareYear === selectedYear;
  }, [configuredTimezone, state.selectedDate, state.type, date]);

  const isDisabled = useMemo(() => {
    if (!state.min && !state.max) {
      return false;
    }

    const startDay = startOfYear(date);
    const endDay = endOfYear(date);

    if (
      (state.min && isBefore(endDay, state.min)) ||
      (state.max && isAfter(startDay, state.max))
    ) {
      return true;
    }

    return false;
  }, [date, state.min, state.max]);

  const { tabIndex } = useFocus({ isFocused, itemRef });

  /** Handle click on a year in the month view. */
  function onClick() {
    dispatch({
      type: 'activeDateChange',
      payload: { activeDate: date, changeToView: 'month' },
    });

    dispatch({ type: 'isCalendarBodyFocusedChange', payload: false });
  }

  return {
    ariaLabel,
    tabIndex,
    isCurrent,
    isSelected,
    isDisabled,
    onClick,
  };
}
